import type { AnalyticsMode } from './consent'
import { accountReadiness, type AccountReadiness } from './account-readiness'
import { safeGetItem, safeSetItem } from '@/lib/utils/safe-storage'
import {
  ACQUISITION_DEDUP_KEY,
  ACQUISITION_WINDOW_MS,
  TOUCH_FIELDS,
  UTM_ENUMS,
  boundedRelease,
  readAcquisitionTouch,
  validTouch,
} from './acquisition-touch'
import type { AcquisitionSdk } from './acquisition'

export const ACQUISITION_INSTITUTION_EVENT = 'acquisition_institution_attributed'
export const INSTITUTION_ACQUISITION_FIELDS = [
  ...TOUCH_FIELDS,
  'org_id',
  '$groups',
  'release',
  'attribution_mode',
] as const
export const INSTITUTION_ACQUISITION_TRANSPORT_FIELDS = ['distinct_id', 'token', 'app_env'] as const
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i
export interface InstitutionAcquisitionContext {
  userId: string
  orgId: string
  createdAt: string | null
  isDemo: boolean | null
  isAdmin: boolean
  isSuperAdmin: boolean | null
  viewAs: boolean
}
export function institutionEligible(
  context: InstitutionAcquisitionContext,
  now = Date.now()
): boolean {
  const created =
    context.createdAt && /(?:Z|[+-]\d{2}:\d{2})$/.test(context.createdAt)
      ? Date.parse(context.createdAt)
      : NaN
  return (
    UUID.test(context.orgId) &&
    !!context.userId &&
    context.isAdmin === true &&
    context.isSuperAdmin === false &&
    context.isDemo === false &&
    context.viewAs === false &&
    Number.isFinite(created) &&
    created <= now &&
    now - created < ACQUISITION_WINDOW_MS
  )
}
export function attributeInstitution(
  sdk: AcquisitionSdk,
  context: InstitutionAcquisitionContext,
  readiness: AccountReadiness,
  mode: AnalyticsMode,
  release: string,
  now = Date.now()
): boolean {
  if (
    !sdk.__loaded ||
    mode !== 'full' ||
    !readiness.resolved ||
    !readiness.identified ||
    readiness.userId !== context.userId ||
    !institutionEligible(context, now) ||
    !boundedRelease(release)
  )
    return false
  const touch = readAcquisitionTouch(now)
  if (!touch || Date.parse(touch.first_touch_at) > Date.parse(context.createdAt!)) return false
  let orgIds: string[] = []
  try {
    const previous = JSON.parse(safeGetItem(ACQUISITION_DEDUP_KEY) ?? 'null')
    if (
      previous?.first_touch_at === touch.first_touch_at &&
      Array.isArray(previous.org_ids) &&
      previous.org_ids.length <= 64 &&
      previous.org_ids.every((id: unknown) => typeof id === 'string' && UUID.test(id))
    )
      orgIds = previous.org_ids
  } catch {
    /* malformed dedup cannot introduce attribution data */
  }
  if (orgIds.includes(context.orgId) || orgIds.length >= 64) return false
  let captured: unknown
  try {
    captured = sdk.capture(ACQUISITION_INSTITUTION_EVENT, {
      ...touch,
      utm_source: touch.utm_source ?? null,
      utm_medium: touch.utm_medium ?? null,
      utm_campaign: touch.utm_campaign ?? null,
      org_id: context.orgId,
      $groups: { organization: context.orgId },
      release,
      attribution_mode: 'consented',
    })
  } catch {
    return false
  }
  if (!captured) return false
  // Browser-local suppression only. Cross-browser institution uniqueness is a query concern.
  safeSetItem(
    ACQUISITION_DEDUP_KEY,
    JSON.stringify({ first_touch_at: touch.first_touch_at, org_ids: [...orgIds, context.orgId] })
  )
  return true
}
/** Final SDK-decorated payload boundary: validate business fields, then strip SDK context. */
export function institutionAcquisitionEvent<
  E extends {
    event: string
    properties: Record<string, unknown>
    $set?: unknown
    $set_once?: unknown
  },
>(mode: AnalyticsMode, event: E, now = Date.now()): E | null {
  if (event.event !== ACQUISITION_INSTITUTION_EVENT) return event
  if (mode !== 'full') return null
  const p = event.properties
  const ready = accountReadiness()
  if (
    !ready.resolved ||
    !ready.identified ||
    !ready.userId ||
    !UUID.test(ready.userId) ||
    p.distinct_id !== ready.userId ||
    !['production', 'staging', 'development'].includes(p.app_env as string) ||
    typeof p.token !== 'string' ||
    !/^[a-zA-Z0-9_-]{1,200}$/.test(p.token)
  )
    return null
  const touch = Object.fromEntries(
    TOUCH_FIELDS.filter(
      (key) => p[key] !== undefined && !(key in UTM_ENUMS && p[key] === null)
    ).map((key) => [key, p[key]])
  )
  const groups = p.$groups
  if (
    !validTouch(touch, now) ||
    p.attribution_mode !== 'consented' ||
    !boundedRelease(p.release) ||
    typeof p.org_id !== 'string' ||
    !UUID.test(p.org_id) ||
    !groups ||
    typeof groups !== 'object' ||
    Array.isArray(groups) ||
    Object.keys(groups).length !== 1 ||
    (groups as Record<string, unknown>).organization !== p.org_id
  )
    return null
  const properties: Record<string, unknown> = { $process_person_profile: false, ...touch }
  for (const key of [
    ...INSTITUTION_ACQUISITION_FIELDS,
    ...INSTITUTION_ACQUISITION_TRANSPORT_FIELDS,
  ]) {
    if (p[key] !== undefined && !(key in UTM_ENUMS && p[key] === null)) properties[key] = p[key]
  }
  const safe = { ...event, properties }
  delete safe.$set
  delete safe.$set_once
  return safe
}
